import React, { Suspense } from 'react';

import { Outlet, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { useUI } from '@/stores/useAppStore';
import DefaultPageChatSurface from '@app/components/DefaultPageChatSurface';
import { MobileBottomBar, MobileTopRow } from '@app/components/MobileChrome';
import { SideNav } from '@app/components/SideNav';
import LandingPage from '@app/pages/LandingPage';
import Login from '@app/pages/Login';
import {
  PrivacyPolicy,
  TeleprompterPage,
  TermsOfService,
  Waitlist,
} from '@app/routes/lazyPages';
import { APP_SHELL_CLASS } from '@app/shellClasses';
import { useEmailNotificationStream } from '@features/email/hooks/useEmailNotificationStream';
import { SeasonalLayer } from '@features/seasonal';
import { WorkspaceUrlSync, APP_ENTRY_STATE } from '@features/workspace';
import { PrimaryPane } from '@features/workspace/components/PrimaryPane';
import { SplitLayout } from '@features/workspace/components/SplitLayout';
import { useSelectionRecorder } from '@shared/active-context';
import { pinsSidebarCollapsed } from '@shared/app-identity/appChrome';
import { homePathFor } from '@shared/app-identity/appHomePaths';
import { useAppBadge } from '@shared/app-identity/useAppBadge';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { useAppUpdateChecker } from '@shared/app-update/useAppUpdateChecker';
import { ChatDock } from '@shared/components/chat/dock/ChatDock';
import { ChatEdgeHandle } from '@shared/components/chat/dock/ChatEdgeHandle';
import { useChatDockShortcut } from '@shared/components/chat/dock/useChatDockShortcut';
import LoadingSpinner from '@shared/components/feedback/LoadingSpinner';
import { OfflineBanner, OfflineNotice } from '@shared/components/offline';
import { Z } from '@shared/constants/zIndex';
import { useDocumentTitle } from '@shared/hooks/useDocumentTitle';
import { useGlobalSearchShortcut } from '@shared/search/hooks/useGlobalSearchShortcut';

interface AppRouterProps {
  isAuthenticated: boolean;
  hasTokenInUrl: boolean;
}

/** Chromeless layout — auth-guarded but no sidebar, topbar, search, or FAB. */
const ChromelessLayout: React.FC = () => (
  <Suspense fallback={<LoadingSpinner className="h-full" message="" />}>
    <Outlet />
  </Suspense>
);

/**
 * Layout for the signed-out routes (`/waitlist`, `/privacy`, `/terms`), which
 * have no shell to preserve — a fullscreen spinner is the whole page here.
 * `/` and `/signin` are not under it: LandingPage and Login stay eagerly
 * imported so a signed-out visitor's first paint costs no extra round trip.
 */
const PublicLayout: React.FC = () => (
  <Suspense fallback={<LoadingSpinner fullscreen message="Loading..." />}>
    <Outlet />
  </Suspense>
);

/** Authenticated layout with frosted-glass header, sidebar, and content. */
const AuthenticatedLayout: React.FC = () => {
  useDocumentTitle();
  // The context object's selection recorder (nav spec §4.7): app-wide, once.
  useSelectionRecorder();
  // The Inbox PWA's icon badge (nav spec §4.9): the inbox count, cleared at zero.
  useAppBadge();
  // Notices a new deploy: reloads a hidden tab, or shows the rail's update chip.
  useAppUpdateChecker();
  // The one email SSE stream for the whole tab: views subscribe to it, and it
  // keeps every poller (nav counts, inbox) on its relaxed cadence on any view.
  useEmailNotificationStream();
  const { isSidebarCollapsed } = useUI();
  const location = useLocation();
  // Cmd/Ctrl-K-style dock shortcut. Route-aware: suppressed on the standalone /chat
  // page, and resolves no default surface on Writing-Workspace routes.
  useChatDockShortcut(location.pathname);
  useGlobalSearchShortcut();

  // Mirrors SideNav's own pin: the column width lives here, so without this the
  // rail would render collapsed inside a 240px gap.
  const pinnedCollapsed = pinsSidebarCollapsed(useInstalledApp());
  const sidebarWidth = pinnedCollapsed || isSidebarCollapsed ? 64 : 240;

  return (
    <div
      data-app-shell
      className={APP_SHELL_CLASS}
    >
      {/* Seasonal ambient decoration (e.g. idle snow). Fixed overlay, no layout impact. */}
      <SeasonalLayer />
      {/* Main row: sidebar + content (fills full height) */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Desktop sidebar — absolutely positioned so it spans viewport top to
            bottom. No header at the `nav` breakpoint and up, so it starts at the
            very top. */}
        <div
          className="absolute inset-y-0 left-0 hidden overflow-hidden transition-all duration-300 ease-in-out nav:block"
          style={{ width: sidebarWidth, zIndex: Z.STICKY }}
        >
          <SideNav />
        </div>
        <div
          className="hidden shrink-0 transition-all duration-300 ease-in-out nav:block"
          style={{ width: sidebarWidth }}
        />

        {/* Main content. Narrow viewports get a top row (Go box, status dot,
            avatar, chat toggle) and a bottom bar (stars + More) in flow around
            the page; both hide at the `nav` breakpoint, where the sidebar owns
            them. WorkspaceUrlSync renders nothing; it keeps the active tab and
            the URL in step.
            Outlet renders the matched child route's layout+page. */}
        <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
          <MobileTopRow />
          {/* Desktop chat opener: a handle on this column's right edge, so it
              rides the dock's left edge when open. Narrow viewports mount
              GlobalChatToggle in the MobileTopRow instead. */}
          <ChatEdgeHandle />
          {/* Offline state, above the content rather than over it: a strip
              pushes the page down instead of covering the row it lands on. */}
          <OfflineBanner />
          <OfflineNotice />
          <SplitLayout>
            <WorkspaceUrlSync />
            <PrimaryPane />
          </SplitLayout>
          <MobileBottomBar />
        </div>

        <ChatDock />
        <DefaultPageChatSurface />
      </div>
    </div>
  );
};

/**
 * A root-level landing (`/`, `/signin`): authenticated visitors bounce to the
 * running app's home carrying APP_ENTRY_STATE, which tells WorkspaceUrlSync it may
 * restore the last open tab. Deep links carry no such state, so the URL the
 * visitor asked for survives.
 *
 * App-aware because the Google OAuth callback returns to `/?token=…` — the one
 * path by which an installed app reaches a root entry. A fixed default sent
 * every app to the inbox on its first launch after signing in.
 *
 * Exported so the redirect is testable without rendering the whole app.
 */
export const EntryRoute: React.FC<{
  isAuthenticated: boolean;
  fallback: React.ReactElement;
}> = ({ isAuthenticated, fallback }) => {
  const slug = useInstalledApp();
  return isAuthenticated ? (
    <Navigate to={homePathFor(slug)} replace state={APP_ENTRY_STATE} />
  ) : (
    fallback
  );
};

/**
 * The app's route tree as a plain function, not a component.
 *
 * `routeTableParity.test.tsx` walks this element tree without mounting the app.
 * A component can't be called that way once the React Compiler has put a
 * memo-cache hook in it; a lowercase function holds no hooks and isn't compiled.
 */
export function buildAppRoutes({ isAuthenticated, hasTokenInUrl }: AppRouterProps): React.ReactElement {
  return (
    <Routes>
      <Route path="/" element={<EntryRoute isAuthenticated={isAuthenticated} fallback={<LandingPage />} />} />
      <Route path="/signin" element={<EntryRoute isAuthenticated={isAuthenticated} fallback={<Login />} />} />
      <Route element={<PublicLayout />}>
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Route>

      {/* Chromeless auth — auth-guarded but no sidebar, topbar, search, or FAB */}
      <Route
        element={
          isAuthenticated ? (
            <ChromelessLayout />
          ) : hasTokenInUrl ? (
            <LoadingSpinner fullscreen message="Loading..." />
          ) : (
            // Render sign-in AT the current path. Navigating to /signin leaves
            // every installed app's manifest scope, ejecting the app into a
            // browser view — and a sign-in performed there may not authenticate
            // the app itself. Staying put also returns the user where they were.
            <Login />
          )
        }
      >
        <Route path="/teleprompter/:itemId" element={<TeleprompterPage />} />
      </Route>

      {/* Every authenticated page renders from APP_ROUTES inside the shell, so
          the router matches the shell rather than the page. `*` ranks below
          every static and dynamic path above it, so the public and chromeless
          routes still win; PrimaryPane redirects when the table has no match,
          which is where the old `path="*"` catch-all went. */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <AuthenticatedLayout />
          ) : hasTokenInUrl ? (
            <LoadingSpinner fullscreen message="Loading..." />
          ) : (
            // Render sign-in AT the current path. Navigating to /signin leaves
            // every installed app's manifest scope, ejecting the app into a
            // browser view — and a sign-in performed there may not authenticate
            // the app itself. Staying put also returns the user where they were.
            <Login />
          )
        }
      />
    </Routes>
  );
}

const AppRouter: React.FC<AppRouterProps> = props => buildAppRoutes(props);

export default AppRouter;
